import { IntegrationError } from '@botpress/webchat-client'

export const MISSING_CLIENT_ID = 'The Client ID is required to initialize the webchat.'
export const MISSING_API_URL = 'The API url is required to initialize the webchat.'
export const MISSING_BOT_ID = 'The Bot ID is required to initialize the webchat.'
export const MISSING_CONFIG_URL = 'No config URL provided.'
export const INVALID_API_URL = 'Invalid API URL.\nPlease use a valid API URL.'
export const INVALID_CLIENT_ID = 'Invalid Client ID.\nPlease use a valid Botpress Client ID.'
export const INVALID_BOTPRESS_URL = 'Invalid URL.\nPlease make sure your client ID and API URL are valid'
export const INVALID_CONFIG_URL = 'Invalid config URL.\nPlease make sure your config URL is valid'
export const INVALID_CONFIGURATION = 'Invalid webchat configuration.\nPlease make sure your config file is valid'
export const CONFIG_ACCESS_DENIED =
  'Access denied to configuration file. Please check your credentials or your configuration URL and try again.'

export const BOT_IS_BLOCKED = 'This bot has temporarily paused.\nPlease check back later.'
export const QUOTA_EXCEEDED = 'This bot has temporarily paused.\nPlease check back later.'
export const NOT_A_PARTICIPANT =
  "Something went wrong. To fix it, please clear your browser's local and session storage and then refresh the page"

export const NETWORK_ERROR = 'Please check your internet connection and refresh the page.'

export const SSE_DISCONNECTED_ERROR = "You've been disconnected from the conversation.\nPlease refresh the page."
export const INVALID_MESSAGE_CONTENT_TYPE = 'Unable to process the message.\nPlease start a new conversation.'

export type WebchatError = (
  | {
      type: 'configuration'
      message:
        | typeof MISSING_CLIENT_ID
        | typeof MISSING_API_URL
        | typeof MISSING_BOT_ID
        | typeof INVALID_CLIENT_ID
        | typeof INVALID_CONFIGURATION
        | typeof MISSING_CONFIG_URL
    }
  | {
      type: 'bot'
      message: typeof BOT_IS_BLOCKED | typeof QUOTA_EXCEEDED | typeof NOT_A_PARTICIPANT
    }
  | { type: 'network'; message: typeof NETWORK_ERROR | typeof SSE_DISCONNECTED_ERROR }
  | { type: string; message: string }
) & { id?: string }

export const webhookHandlerErrorMap: {
  [key in 'ResourceNotFound' | 'Forbidden']: WebchatError
} = {
  ResourceNotFound: {
    type: 'configuration',
    message: INVALID_CLIENT_ID,
  },
  Forbidden: {
    type: 'bot',
    message: BOT_IS_BLOCKED,
  },
}

export const commonApiErrorMap: {
  [key in 'MethodNotFound' | 'QuotaExceeded' | 'Forbidden']: WebchatError
} = {
  MethodNotFound: {
    type: 'configuration',
    message: INVALID_BOTPRESS_URL,
  },
  QuotaExceeded: {
    type: 'bot',
    message: QUOTA_EXCEEDED,
  },
  Forbidden: {
    type: 'bot',
    message: NOT_A_PARTICIPANT,
  },
}

export function isWebhookHandlerErrorType(type: string): type is keyof typeof webhookHandlerErrorMap {
  return type in webhookHandlerErrorMap
}

export function isCommonApiErrorType(type: string): type is keyof typeof commonApiErrorMap {
  return type in commonApiErrorMap
}

export function isIntegrationError(thrown: unknown): thrown is IntegrationError {
  return thrown !== null && typeof thrown === 'object' && 'id' in thrown && 'code' in thrown && 'type' in thrown
}

type EventError = Error & { type: 'error' }

export function isEventError(thrown: unknown): thrown is EventError {
  return (
    thrown !== null &&
    typeof thrown === 'object' &&
    'type' in thrown &&
    thrown.type === 'error' &&
    'message' in thrown &&
    typeof thrown.message === 'string'
  )
}
